import { IMetric, Property } from "@dto/architecture.dto";
import { IWithUnit, UnitPrefix } from "@dto/pageResponse.dto";
import { Unit } from "@dto/subsystemMetricsResponse.dto";
import { UnitUtil } from "@dto/util/UnitUtil";
import { DefaultOptionType } from "antd/es/select";

type RemoveIndex<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : symbol extends K ? never : K]: T[K];
};

/**
 *  Generic unit based option for select component
 */
export interface UnitOption<T extends Partial<IWithUnit>> extends RemoveIndex<DefaultOptionType> {
  text: string;
  shortcut: string;
  prefix: UnitPrefix;
  unit: Unit;
  base: T;
}

/**
 * Static unit helper rendering functions
 */
export abstract class UnitHelper {

  /**
   * Returns table column header
   * @param colName column name
   * @param unitSizedShortcut column unit shortcut
   * @returns
   */
  static formatUnitColHeader(colName: string, unitSizedShortcut: string) {
    if (!unitSizedShortcut || colName === unitSizedShortcut) {
      return colName;
    }

    const separator = colName.endsWith('\n') ? '' : ' ';
    return `${colName}${separator}[${unitSizedShortcut}]`;
  }

  /**
   * Convert metric to select component option with appended unit shortcut
   * @param metric to extract
   * @returns MetricOption
   */
  static metric2option(metric: IMetric): UnitOption<IMetric> {
    const prefix = UnitHelper.getMetricPrefix(metric);
    const prefixShortcut = UnitHelper.getMetricShortcut(metric, prefix);
    return {
      value: metric.metric,
      text: metric.label,
      label: UnitHelper.getMetricOptionLabel(metric.label, prefixShortcut),
      shortcut: prefixShortcut,
      prefix,
      unit: metric.unit,
      base: metric
    };
  }

  /**
   * Convert property to select component option with appended unit shortcut
   * @param property to extract
   * @returns DefaultOptionType
   */
  static property2option(property: Property): UnitOption<Property> {
    const prefix = UnitHelper.getMetricPrefix(property);
    const prefixShortcut = UnitHelper.getMetricShortcut(property, prefix);
    return {
      value: property.property_name,
      text: property.label,
      label: UnitHelper.getMetricOptionLabel(property.label, prefixShortcut),
      shortcut: prefixShortcut,
      prefix,
      unit: property.unit,
      base: property
    };
  }

  /**
   * Gets default unit prefix
   * @param metric to get unit from
   * @returns UnitPrefix
   */
  static getMetricPrefix(metric: Partial<IWithUnit>) {
    if (!metric) return;
    return metric.defaultPrefix || UnitUtil.sizeUnit(0, metric.shortcut, metric.unit).unitSizePrefix;
  }

  /**
   * Gets unit shortcut with unit prefix
   * @param metric to work with
   * @param prefix gets default unit prefix if not provided
   * @returns string
   */
  static getMetricShortcut(metric: Partial<IWithUnit>, prefix = UnitHelper.getMetricPrefix(metric)) {
    return (prefix || '') + (metric?.shortcut || '');
  }

  /**
   * Returns metric label with unit shortcut
   * @param metricLabel label of metric
   * @param shortcut unit shortcut to float at right
   * @returns JSX element
   */
  static getMetricOptionLabel(metricLabel: string, shortcut: string) {
    return <div>{metricLabel}<span className='xm-item-option-detail'>{shortcut}</span></div>;
  }

}